import React, { useState } from 'react'
import styled from 'styled-components'
import { Know, KnowDocument, KnowTranscript } from 'interfaces/Know'
import { knowContentPreviewUrl } from 'lib/formatting/content'
import PDFIcon from 'assets/icons/pdf.svg'
import FlashcardIcon from 'assets/icons/flashcard.svg'
import dynamic from 'next/dynamic'
import KnowContentConversionBanner from './KnowContentConversionBanner'
import { useEffect } from 'react'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { ButtonText } from 'stories/elements/Typography/Text'
import MobileFixedConversionBanner from '../../KnowMobileFixedConversionBanner'
import { useToggleState } from 'lib/hooks/useToggleState'
import NewButton from 'components/elements/NewButton'
import theme from 'stories/utils/theme'
import _useTranslation from 'lib/hooks/_useTranslation'
import { getAltTextFromTranscript } from 'lib/features/Know/know'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import { getImageMeta } from 'lib/conversion/image'
import { useRouter } from 'next/router'
import CloseIcon from 'assets/icons/close.svg'
import { getMobileOS } from 'lib/tracking/attribution/device'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import { isDeviceMobile } from 'lib/features/deviceInfo'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import KnowContentPreviewSidebar from './KnowContentPreviewSidebar'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { getCountryConfig } from 'lib/constants/countryconfig'
import { parseLocale } from 'lib/constants/localization'

const KnowPDFViewer = dynamic(() => import('components/modules/PDFPreview/KnowPDFViewer'), { ssr: false })

const ContentGrid = styled.div<{ centerPreview?: boolean }>`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: end;
    padding: 0 10px;
    justify-content: ${({ centerPreview }) => (centerPreview ? 'center' : 'start')};
    gap: 10px;
`

const Wrapper = styled.div`
    position: relative;
`

const KnowPreviewButton = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    margin: 0 auto;
    bottom: 15px;
    cursor: pointer;

    button {
        display: flex;
        justify-content: center;
    }
`

const PreviewImage = styled.img`
    cursor: pointer;
    height: auto;
    min-width: 250px;
    max-height: 400px;
    max-width: 550px;
    width: auto;
    object-fit: contain;
    object-position: top;
    margin: 0 5px;
    border-radius: ${theme.borderRadius.content};
    background-color: ${theme.colors.white};
`

const ConversionWrapper = styled.div`
    position: relative;
    overflow: hidden;
    border-bottom-left-radius: ${theme.borderRadius.content};
    border-bottom-right-radius: ${theme.borderRadius.content};
`

const Close = styled(CloseIcon)`
    position: fixed;
    right: 15px;
    top: 15px;
    z-index: ${theme.zIndex.FAB};
    cursor: pointer;
`

const PreviewContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    position: relative;
    padding: 25px 0;
    min-height: 80vh !important;
`

export const NoPreviewWrapper = styled.div`
    cursor: pointer;
    height: 400px;
    width: 250px;
    background: ${theme.colors.backgroundTransparent};
    margin: 0 5px;
    display: grid;
    place-content: center;
    border-radius: ${theme.borderRadius.content};

    & svg {
        fill: white;
        width: 50px;
        height: 50px;
    }
`

const PreviewImageContainer = (props: { content: KnowDocument; transcript: KnowTranscript | null }) => {
    const [imageWidth, setImageWidth] = useState<string | number>('100%')
    const previewUrl = knowContentPreviewUrl(props.content)

    const maxWidth = +imageWidth > 400 ? 400 : imageWidth

    const showFlashcardsIcon = !props.content?.contentUrl

    useEffect(() => {
        previewUrl && getImageMeta(previewUrl).then(({ width, height }) => setImageWidth((width / height) * 400))
    }, [previewUrl])

    if (previewUrl) {
        return (
            <PreviewImage
                src={previewUrl}
                height={400}
                width={maxWidth}
                loading="eager"
                alt={getAltTextFromTranscript(props.transcript)}
            />
        )
    } else {
        return <NoPreviewWrapper>{showFlashcardsIcon ? <FlashcardIcon /> : <PDFIcon />}</NoPreviewWrapper>
    }
}

interface Props {
    knowSeoInfo: KnowSeoInfo
    similarKnows: Know[] | null
    showFixedBanner: boolean
    trackContentOpenEvent: () => void
    onContentConversionBannerButtonClick(platform: 'android' | 'ios'): void
    disableExperiments?: boolean
    transcript?: KnowTranscript | null
    isDarkTheme?: boolean
}

const KnowPreviewSection = (props: Props) => {
    const [showPreviewModal, togglePreviewModal] = useToggleState(false)
    const [showFullScreen, toggleFullScreen] = useToggleState(false)
    const [selectedContent, setSelectedContent] = useState<KnowDocument | null>()
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const md = useMedia(deviceSize.md)
    const mobileOS = getMobileOS() ?? 'ios'
    const { t: tCommon } = _useTranslation('common')

    const { know } = props.knowSeoInfo

    const removePadding = !authenticatedUser

    const router = useRouter()
    const isMobile = isDeviceMobile()
    const parsedLocale = parseLocale(router.locale)
    const { unblurContentForSmallerMarkets } = getCountryConfig(parsedLocale.countryCode)

    const fullyUnblurPDFPagesForSmallerMarkets = unblurContentForSmallerMarkets

    const countryLanguageConfig = getCountryLanguageConfig(router.locale)
    const isLaunched = !!countryLanguageConfig?.isLaunched

    const showDesktopSidebar = !isMobile && !authenticatedUser

    const unblurContent = !!(!isMobile && authenticatedUser) || fullyUnblurPDFPagesForSmallerMarkets

    const handleShowContent = (content?: KnowDocument) => {
        props.trackContentOpenEvent()

        content && setSelectedContent(content)
        togglePreviewModal()
    }

    const handleClose = () => {
        togglePreviewModal()
    }

    const downloadDeepLinkPath = appDeepLinkKnow(know.uuid)

    return (
        <>
            <ContentGrid centerPreview={know.documents?.length < 2}>
                {know.documents &&
                    know.documents.map((content, i) => (
                        <Wrapper key={i} onClick={() => handleShowContent(content)}>
                            <PreviewImageContainer content={content} transcript={props.transcript ?? null} />

                            <KnowPreviewButton>
                                <NewButton
                                    width={175}
                                    height={50}
                                    fullWidth={false}
                                    borderRadius={theme.borderRadius.normal}
                                    color={
                                        props.isDarkTheme
                                            ? theme.colors.grayScalePrimaryTransparent
                                            : theme.colors.deepSeaBlue
                                    }
                                    isWebapp={props.isDarkTheme}
                                >
                                    <ButtonText color={theme.colors.white}>{tCommon('common/viewContent')}</ButtonText>
                                </NewButton>
                            </KnowPreviewButton>
                        </Wrapper>
                    ))}

                {know.markdown && (
                    <Wrapper onClick={() => handleShowContent()}>
                        <NoPreviewWrapper>
                            <PDFIcon />
                        </NoPreviewWrapper>
                        <KnowPreviewButton>
                            <NewButton
                                width={175}
                                height={50}
                                fullWidth={false}
                                borderRadius={theme.borderRadius.normal}
                                color={
                                    props.isDarkTheme ? theme.colors.grayScalePrimaryTransparent : theme.colors.deepSeaBlue
                                }
                                isWebapp={props.isDarkTheme}
                            >
                                <ButtonText color={theme.colors.white}>{tCommon('common/viewContent')}</ButtonText>
                            </NewButton>
                        </KnowPreviewButton>
                    </Wrapper>
                )}

                {showPreviewModal && (selectedContent || know.markdown) ? (
                    <FeatureDialog
                        open={showPreviewModal}
                        onClose={handleClose}
                        ariaPrefix="know-content-dialog"
                        backgroundColor={'linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);'}
                        hideCloseButton
                        hideBorderRadius={md}
                        hideTitle
                        title=""
                        maxWidth={showDesktopSidebar ? 1400 : 1250}
                        allowVerticalOverflow
                        allowHorizontalOverflow
                        toggleFullScreen={!showFullScreen}
                        fullScreen={md || showFullScreen}
                        noPadding={removePadding}
                        enableScrollLock
                        dialogFooter={
                            props.showFixedBanner && mobileOS && isLaunched && removePadding ? (
                                <ConversionWrapper>
                                    {md ? (
                                        <MobileFixedConversionBanner
                                            isDialog
                                            onDownloadButtonClick={props.onContentConversionBannerButtonClick}
                                            mobileOS={mobileOS}
                                            downloadDeepLinkPath={downloadDeepLinkPath}
                                        />
                                    ) : (
                                        <KnowContentConversionBanner
                                            onDownloadButtonClick={props.onContentConversionBannerButtonClick}
                                            know={know}
                                        />
                                    )}
                                </ConversionWrapper>
                            ) : undefined
                        }
                    >
                        <PreviewContainer>
                            {md ? <Close onClick={handleClose} /> : null}

                            {props.knowSeoInfo ? (
                                <KnowPDFViewer
                                    knowSeoInfo={props.knowSeoInfo}
                                    showOverflow
                                    toggleFullScreen={toggleFullScreen}
                                    isPreview={unblurContent}
                                />
                            ) : null}

                            {showDesktopSidebar ? (
                                <KnowContentPreviewSidebar
                                    know={know}
                                    similarKnows={props.similarKnows}
                                    closePreview={handleClose}
                                />
                            ) : null}
                        </PreviewContainer>
                    </FeatureDialog>
                ) : null}
            </ContentGrid>
        </>
    )
}

export default KnowPreviewSection
